/**
 * WorkedExample — a structured "scenario" panel for explainers.
 *
 * Where `CalloutBox` carries a single short statement, a `WorkedExample`
 * walks the reader through a concrete situation: the scenario, what an
 * attacker sees, and what the categorical structure rules out. Each slot
 * is optional; pass only the ones that fit the explainer's story.
 *
 * The result is a tinted panel with consistent typography across the
 * categorical-structure family, so a reader who has learned the visual
 * grammar from one card can find their footing on the next.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { FONT, GRAY, withAlpha } from "../../design-system/tokens.js";

export interface WorkedExampleProps {
  /** Small uppercase kicker rendered above the headline. */
  readonly eyebrow: string;
  /** Single-line headline naming the scenario. */
  readonly title: string;
  /** Accent color (the box tint and the eyebrow color). */
  readonly accent: string;
  /** The scenario set-up — what is being modeled. */
  readonly scenario: React.ReactNode;
  /** Optional "what an attacker can do without this structure" framing. */
  readonly withoutStructure?: React.ReactNode;
  /** Optional "what the structure makes inexpressible" framing. */
  readonly withStructure?: React.ReactNode;
  /** Optional accessible label override. */
  readonly ariaLabel?: string;
}

/** Section label colors and labels for the optional with/without slots. */
const SECTION = {
  withoutStructure: { label: "Without the structure" },
  withStructure: { label: "With the structure" },
} as const;

export const WorkedExample = React.memo(function WorkedExample({
  eyebrow,
  title,
  accent,
  scenario,
  withoutStructure,
  withStructure,
  ariaLabel,
}: WorkedExampleProps): React.JSX.Element {
  return (
    <section
      aria-label={ariaLabel ?? `${eyebrow}: ${title}`}
      style={{
        marginTop: "0.75rem",
        padding: "0.875rem",
        backgroundColor: withAlpha(accent, "14"),
        border: `1px solid ${withAlpha(accent, "55")}`,
        borderRadius: "0.5rem",
      }}
    >
      <header style={{ marginBottom: "0.375rem" }}>
        <div
          style={{
            fontSize: "0.6875rem",
            fontWeight: 700,
            color: accent,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {eyebrow}
        </div>
        <div
          style={{
            fontFamily: FONT.heading,
            fontSize: "1rem",
            fontWeight: 700,
            color: GRAY[900],
            marginTop: "0.125rem",
          }}
        >
          {title}
        </div>
      </header>
      <div style={{ color: GRAY[700], fontSize: "0.875rem", lineHeight: 1.55 }}>{scenario}</div>
      {withoutStructure !== undefined && (
        <SubSection accent={accent} label={SECTION.withoutStructure.label} variant="problem">
          {withoutStructure}
        </SubSection>
      )}
      {withStructure !== undefined && (
        <SubSection accent={accent} label={SECTION.withStructure.label} variant="resolution">
          {withStructure}
        </SubSection>
      )}
    </section>
  );
});

function SubSection({
  accent,
  label,
  variant,
  children,
}: {
  readonly accent: string;
  readonly label: string;
  readonly variant: "problem" | "resolution";
  readonly children: React.ReactNode;
}): React.JSX.Element {
  // "Problem" rows are flat (neutral chrome) so the reader's eye lands on
  // the "resolution" row, which carries the accent border.
  const borderColor = variant === "resolution" ? withAlpha(accent, "66") : GRAY[300];
  return (
    <div
      style={{
        marginTop: "0.5rem",
        padding: "0.5rem 0.625rem",
        background: "white",
        borderLeft: `3px solid ${borderColor}`,
        borderRadius: "0.25rem",
      }}
    >
      <div
        style={{
          fontSize: "0.6875rem",
          fontWeight: 700,
          color: variant === "resolution" ? accent : GRAY[600],
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          marginBottom: "0.25rem",
        }}
      >
        {label}
      </div>
      <div style={{ color: GRAY[700], fontSize: "0.8125rem", lineHeight: 1.5 }}>{children}</div>
    </div>
  );
}
